import { useState } from 'react';
import { FiX } from 'react-icons/fi';
import { apiCreateCoupon, apiUpdateCoupon } from '../../api';
import { getCouponTickerMessage } from '../../utils/couponTicker';

export const CouponModal = ({ coupon, onClose, onSaved }) => {
  const [form, setForm] = useState({
    code: coupon?.code || '',
    tickerMessage: coupon?.tickerMessage || '',
    type: coupon?.type || 'percent',
    value: coupon?.value || '',
    minOrder: coupon?.minOrder || '',
    maxDiscount: coupon?.maxDiscount || '',
    usageLimit: coupon?.usageLimit || '',
    expiresAt: coupon?.expiresAt ? coupon.expiresAt.split('T')[0] : '',
    isActive: coupon?.isActive !== undefined ? coupon.isActive : true,
  });
  const [saving, setSaving] = useState(false);
  const tickerPreview = getCouponTickerMessage(form);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: form.code,
        tickerMessage: form.tickerMessage.trim() || null,
        type: form.type,
        value: parseFloat(form.value),
        minOrder: form.minOrder ? parseFloat(form.minOrder) : 0,
        maxDiscount: form.maxDiscount ? parseFloat(form.maxDiscount) : null,
        usageLimit: form.usageLimit ? parseInt(form.usageLimit) : null,
        expiresAt: form.expiresAt || null,
        isActive: form.isActive,
      };

      if (coupon) {
        await apiUpdateCoupon(coupon.id, payload);
      } else {
        await apiCreateCoupon(payload);
      }
      onSaved();
    } catch { /* API client displays the error toast. */ } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-black">
            {coupon ? 'Edit Coupon' : 'Add Coupon'}
          </h2>
          <button onClick={onClose} className="text-black hover:text-black">
            <FiX size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-black mb-1">Coupon Code *</label>
            <input
              type="text"
              required
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500 font-mono"
              placeholder="e.g. SAVE20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-black mb-1">
              Ticker Message <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <textarea
              rows={3}
              maxLength={200}
              value={form.tickerMessage}
              onChange={(e) => setForm({ ...form, tickerMessage: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
              placeholder="e.g. Special offer: use BOOK10 and save 10% today!"
            />
            <p className="mt-1 text-right text-xs text-gray-500">
              {form.tickerMessage.length}/200
            </p>
            <div className="mt-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-green-700">
                Frontend ticker preview
              </p>
              <p className="mt-1 text-sm font-medium text-green-950">{tickerPreview}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-black mb-1">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
              >
                <option value="percent">Percentage (%)</option>
                <option value="flat">Fixed Amount (₹)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-black mb-1">Value *</label>
              <input
                type="number"
                required
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                placeholder={form.type === 'percent' ? 'e.g. 20' : 'e.g. 100'}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-black mb-1">Min Order (₹)</label>
              <input
                type="number"
                value={form.minOrder}
                onChange={(e) => setForm({ ...form, minOrder: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-black mb-1">
                Max Discount (₹)
              </label>
              <input
                type="number"
                value={form.maxDiscount}
                onChange={(e) => setForm({ ...form, maxDiscount: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                placeholder="No limit"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-black mb-1">Usage Limit</label>
              <input
                type="number"
                value={form.usageLimit}
                onChange={(e) => setForm({ ...form, usageLimit: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                placeholder="Unlimited"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-black mb-1">Expires At</label>
              <input
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="rounded"
            />
            Active
          </label>
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : coupon ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
